import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Select, InputNumber,
  message, Typography, Row, Col, DatePicker, Popconfirm, Input, Drawer
} from 'antd';
import {
  SearchOutlined, EditOutlined, PauseCircleOutlined, PlayCircleOutlined,
  ClockCircleOutlined, CreditCardOutlined, ReloadOutlined, BankOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import superAdminApi, { AdminSubscription, AdminPlan, PaginationParams } from '../../api/superAdminApi';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import MobileDataList from '../../../../components/mobile/MobileDataList';

const { Title, Text } = Typography;
const { Option } = Select;

const SubscriptionManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterPlan, setFilterPlan] = useState<string | undefined>();
  const [overrideModalVisible, setOverrideModalVisible] = useState(false);
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<AdminSubscription | null>(null);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [viewingSubscription, setViewingSubscription] = useState<AdminSubscription | null>(null);
  const [form] = Form.useForm();
  const [extendForm] = Form.useForm();

  const fetchSubscriptions = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getSubscriptions({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        search: searchText || undefined,
        status: filterStatus,
        planId: filterPlan,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setSubscriptions(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  }, [searchText, filterStatus, filterPlan, pagination.page, pagination.limit]);

  const fetchPlans = async () => {
    try {
      const response = await superAdminApi.getPlans(true);
      if (response.success) {
        setPlans(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch plans', error);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, []);

  const handleTableChange = (paginationConfig: any) => {
    fetchSubscriptions({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  const handleOverride = (record: AdminSubscription) => {
    setSelectedSubscription(record);
    form.setFieldsValue({
      maxUsers: record.maxUsers,
      maxLeads: record.maxLeads,
      maxAIQueries: record.maxAIQueries,
      maxEmailCampaigns: record.maxEmailCampaigns,
      amount: Number(record.amount),
      billingCycle: record.billingCycle,
      currentPeriodEnd: dayjs(record.currentPeriodEnd)
    });
    setOverrideModalVisible(true);
  };

  const handleOverrideSubmit = async (values: any) => {
    if (!selectedSubscription) return;
    try {
      await superAdminApi.overrideSubscription(selectedSubscription.companyId, {
        ...values,
        currentPeriodEnd: values.currentPeriodEnd?.toISOString()
      });
      message.success('Subscription updated successfully');
      setOverrideModalVisible(false);
      fetchSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to update subscription');
    }
  };

  const handleExtend = (record: AdminSubscription) => {
    setSelectedSubscription(record);
    extendForm.resetFields();
    setExtendModalVisible(true);
  };

  const handleExtendSubmit = async (values: any) => {
    if (!selectedSubscription) return;
    try {
      await superAdminApi.extendSubscription(selectedSubscription.id, values.days);
      message.success(`Subscription extended by ${values.days} days`);
      setExtendModalVisible(false);
      fetchSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to extend subscription');
    }
  };

  const handleSuspend = async (record: AdminSubscription) => {
    try {
      await superAdminApi.suspendSubscription(record.id);
      message.success('Subscription suspended');
      fetchSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to suspend subscription');
    }
  };

  const handleReactivate = async (record: AdminSubscription) => {
    try {
      await superAdminApi.reactivateSubscription(record.id);
      message.success('Subscription reactivated');
      fetchSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to reactivate subscription');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'green';
      case 'PAST_DUE': return 'orange';
      case 'SUSPENDED': return 'red';
      case 'CANCELLED': return 'default';
      case 'EXPIRED': return 'default';
      default: return 'default';
    }
  };

  const columns: ColumnsType<AdminSubscription> = [
    {
      title: 'Company',
      key: 'company',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.companyName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.companyCode}</Text>
        </Space>
      )
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <Tag color="blue">{record.planDisplayName || record.plan}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>${Number(record.amount).toLocaleString()}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.billingCycle}</Text>
        </Space>
      )
    },
    {
      title: 'Period End',
      dataIndex: 'currentPeriodEnd',
      key: 'currentPeriodEnd',
      width: 120,
      render: (date) => {
        const endDate = new Date(date);
        const isExpiringSoon = endDate.getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;
        return (
          <Text type={isExpiringSoon ? 'warning' : undefined}>
            {endDate.toLocaleDateString()}
          </Text>
        );
      }
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <Text>AI: {record.currentAIQueries || 0}/{record.maxAIQueries || '∞'}</Text>
          <Text>Email: {record.currentEmailsSent || 0}/{record.maxEmailCampaigns || '∞'}</Text>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOverride(record)}
            title="Override"
          />
          <Button
            type="text"
            icon={<ClockCircleOutlined />}
            onClick={() => handleExtend(record)}
            title="Extend"
          />
          {record.status === 'ACTIVE' ? (
            <Popconfirm
              title="Suspend this subscription?"
              onConfirm={() => handleSuspend(record)}
            >
              <Button
                type="text"
                icon={<PauseCircleOutlined />}
                danger
                title="Suspend"
              />
            </Popconfirm>
          ) : record.status === 'SUSPENDED' && (
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              onClick={() => handleReactivate(record)}
              title="Reactivate"
              style={{ color: '#52c41a' }}
            />
          )}
        </Space>
      )
    }
  ];

  // Handle view subscription details (mobile)
  const handleView = (subscription: AdminSubscription) => {
    setViewingSubscription(subscription);
    setDetailDrawerVisible(true);
  };

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'companyName' as const,
    secondaryField: 'companyCode' as const,
    statusField: 'status' as const,
    statusConfig: {
      ACTIVE: { color: 'green', text: 'Active' },
      PAST_DUE: { color: 'orange', text: 'Past Due' },
      SUSPENDED: { color: 'red', text: 'Suspended' },
      CANCELLED: { color: 'default', text: 'Cancelled' },
      EXPIRED: { color: 'default', text: 'Expired' },
    },
    avatar: {
      icon: <BankOutlined />,
    },
    fields: [
      {
        key: 'planDisplayName' as const,
        label: 'Plan',
        render: (v: string, record: AdminSubscription) => (
          <Tag color="blue">{v || record.plan}</Tag>
        ),
      },
      {
        key: 'amount' as const,
        label: 'Amount',
        render: (v: number, record: AdminSubscription) => (
          <span>${Number(v).toLocaleString()} / {record.billingCycle}</span>
        ),
      },
      {
        key: 'currentPeriodEnd' as const,
        label: 'Period End',
        render: (v: string) => {
          const endDate = new Date(v);
          const isExpiringSoon = endDate.getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;
          return (
            <Text type={isExpiringSoon ? 'warning' : undefined}>
              {endDate.toLocaleDateString()}
            </Text>
          );
        },
      },
    ],
    onClick: handleView,
  };

  // Mobile filters
  const mobileFilters = [
    {
      key: 'status',
      placeholder: 'Status',
      options: [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Past Due', value: 'PAST_DUE' },
        { label: 'Suspended', value: 'SUSPENDED' },
        { label: 'Cancelled', value: 'CANCELLED' },
      ],
      value: filterStatus,
      onChange: (v: string) => {
        setFilterStatus(v);
        fetchSubscriptions({ page: 1 });
      },
    },
    {
      key: 'plan',
      placeholder: 'Plan',
      options: plans.map(p => ({ label: p.displayName, value: p.id })),
      value: filterPlan,
      onChange: (v: string) => {
        setFilterPlan(v);
        fetchSubscriptions({ page: 1 });
      },
    },
  ];

  // Mobile view
  if (isMobile) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <CreditCardOutlined /> Subscriptions
          </Title>
        </div>

        <MobileDataList
          data={subscriptions}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No subscriptions found"
          onRefresh={() => fetchSubscriptions()}
          onSearch={(v) => {
            setSearchText(v);
            fetchSubscriptions({ page: 1 });
          }}
          searchPlaceholder="Search company..."
          filters={mobileFilters}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => fetchSubscriptions({ page, limit: pageSize }),
          }}
          swipeActions={{
            right: {
              icon: <EditOutlined />,
              color: '#1890ff',
              label: 'Override',
              onClick: handleOverride,
            },
          }}
        />

        {/* Subscription Detail Drawer (Mobile) */}
        <Drawer
          title={viewingSubscription?.companyName || 'Subscription Details'}
          placement="bottom"
          height="85%"
          open={detailDrawerVisible}
          onClose={() => setDetailDrawerVisible(false)}
          extra={
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setDetailDrawerVisible(false);
                  if (viewingSubscription) handleOverride(viewingSubscription);
                }}
              >
                Override
              </Button>
            </Space>
          }
        >
          {viewingSubscription && (
            <div>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card size="small" title="Company Info">
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div>
                      <Text type="secondary">Company Name</Text>
                      <div><Text strong>{viewingSubscription.companyName}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Company ID</Text>
                      <div><Text>{viewingSubscription.companyCode}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Status</Text>
                      <div>
                        <Tag color={getStatusColor(viewingSubscription.status)}>
                          {viewingSubscription.status}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Plan & Billing">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <Text type="secondary">Plan</Text>
                      <div>
                        <Tag color="blue">
                          {viewingSubscription.planDisplayName || viewingSubscription.plan}
                        </Tag>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">Billing Cycle</Text>
                      <div><Text strong>{viewingSubscription.billingCycle}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Amount</Text>
                      <div><Text strong>${Number(viewingSubscription.amount).toLocaleString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Period End</Text>
                      <div>
                        <Text strong>
                          {new Date(viewingSubscription.currentPeriodEnd).toLocaleDateString()}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Resource Limits">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <Text type="secondary">Max Users</Text>
                      <div><Text strong>{viewingSubscription.maxUsers || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Max Leads</Text>
                      <div><Text strong>{viewingSubscription.maxLeads?.toLocaleString() || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">AI Queries</Text>
                      <div>
                        <Text strong>
                          {viewingSubscription.currentAIQueries || 0} / {viewingSubscription.maxAIQueries || '∞'}
                        </Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">Emails Sent</Text>
                      <div>
                        <Text strong>
                          {viewingSubscription.currentEmailsSent || 0} / {viewingSubscription.maxEmailCampaigns || '∞'}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    block
                    icon={<ClockCircleOutlined />}
                    onClick={() => {
                      setDetailDrawerVisible(false);
                      handleExtend(viewingSubscription);
                    }}
                  >
                    Extend Subscription
                  </Button>
                  {viewingSubscription.status === 'ACTIVE' ? (
                    <Popconfirm
                      title="Suspend this subscription?"
                      onConfirm={() => {
                        handleSuspend(viewingSubscription);
                        setDetailDrawerVisible(false);
                      }}
                    >
                      <Button block danger icon={<PauseCircleOutlined />}>
                        Suspend Subscription
                      </Button>
                    </Popconfirm>
                  ) : viewingSubscription.status === 'SUSPENDED' && (
                    <Button
                      block
                      icon={<PlayCircleOutlined />}
                      style={{ color: '#52c41a', borderColor: '#52c41a' }}
                      onClick={() => {
                        handleReactivate(viewingSubscription);
                        setDetailDrawerVisible(false);
                      }}
                    >
                      Reactivate Subscription
                    </Button>
                  )}
                </Space>
              </Space>
            </div>
          )}
        </Drawer>

        {/* Override Modal (Full screen on mobile) */}
        <Modal
          title={`Override: ${selectedSubscription?.companyName}`}
          open={overrideModalVisible}
          onCancel={() => setOverrideModalVisible(false)}
          onOk={() => form.submit()}
          width="100%"
          style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
          styles={{ body: { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } }}
        >
          <Form form={form} layout="vertical" onFinish={handleOverrideSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maxUsers" label="Max Users">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxLeads" label="Max Leads">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maxAIQueries" label="AI Queries">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxEmailCampaigns" label="Emails/Month">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="amount" label="Amount ($)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="billingCycle" label="Billing Cycle">
                  <Select>
                    <Option value="MONTHLY">Monthly</Option>
                    <Option value="YEARLY">Yearly</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="currentPeriodEnd" label="Period End Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Modal>

        {/* Extend Modal */}
        <Modal
          title={`Extend: ${selectedSubscription?.companyName}`}
          open={extendModalVisible}
          onCancel={() => setExtendModalVisible(false)}
          onOk={() => extendForm.submit()}
        >
          <Form form={extendForm} layout="vertical" onFinish={handleExtendSubmit}>
            <Form.Item
              name="days"
              label="Extend by (days)"
              rules={[{ required: true, message: 'Please enter number of days' }]}
            >
              <InputNumber min={1} max={365} style={{ width: '100%' }} />
            </Form.Item>
            <Text type="secondary">
              Current period ends: {selectedSubscription && new Date(selectedSubscription.currentPeriodEnd).toLocaleDateString()}
            </Text>
          </Form>
        </Modal>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <CreditCardOutlined /> Subscription Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search company..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => fetchSubscriptions({ page: 1 })}
              style={{ width: 200 }}
              allowClear
            />
            <Select
              placeholder="Status"
              style={{ width: 130 }}
              allowClear
              value={filterStatus}
              onChange={(v) => {
                setFilterStatus(v);
                fetchSubscriptions({ page: 1 });
              }}
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="PAST_DUE">Past Due</Option>
              <Option value="SUSPENDED">Suspended</Option>
              <Option value="CANCELLED">Cancelled</Option>
            </Select>
            <Select
              placeholder="Plan"
              style={{ width: 150 }}
              allowClear
              value={filterPlan}
              onChange={(v) => {
                setFilterPlan(v);
                fetchSubscriptions({ page: 1 });
              }}
            >
              {plans.map(p => (
                <Option key={p.id} value={p.id}>{p.displayName}</Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => fetchSubscriptions()}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} subscriptions`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Override Modal */}
      <Modal
        title={`Override Subscription: ${selectedSubscription?.companyName}`}
        open={overrideModalVisible}
        onCancel={() => setOverrideModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleOverrideSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="maxUsers" label="Max Users">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxLeads" label="Max Leads">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="maxAIQueries" label="Max AI Queries">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxEmailCampaigns" label="Max Emails/Month">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount ($)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billingCycle" label="Billing Cycle">
                <Select>
                  <Option value="MONTHLY">Monthly</Option>
                  <Option value="YEARLY">Yearly</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="currentPeriodEnd" label="Period End Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Extend Modal */}
      <Modal
        title={`Extend Subscription: ${selectedSubscription?.companyName}`}
        open={extendModalVisible}
        onCancel={() => setExtendModalVisible(false)}
        onOk={() => extendForm.submit()}
      >
        <Form form={extendForm} layout="vertical" onFinish={handleExtendSubmit}>
          <Form.Item
            name="days"
            label="Extend by (days)"
            rules={[{ required: true, message: 'Please enter number of days' }]}
          >
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">
            Current period ends: {selectedSubscription && new Date(selectedSubscription.currentPeriodEnd).toLocaleDateString()}
          </Text>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionManagement;
